import * as React from 'react';
import { cn } from '@packages/utils';
import { Edge, wwRng, wwTornPath } from './collageMath';

/** Torn scrap — clipped paper with a drop-shadow that follows the torn edge */
export function TornScrap({
  edges = ['bottom', 'top'],
  seed = 7,
  amp = 2.4,
  rotate = 0,
  bg,
  color,
  style = {},
  className = '',
  children,
}: {
  edges?: Edge[];
  seed?: number;
  amp?: number;
  rotate?: number;
  bg?: string;
  color?: string;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        filter: 'drop-shadow(0 7px 14px rgba(27,24,18,.20))',
        transform: `rotate(${rotate}deg)`,
        ...style,
      }}>
      <div
        className={className}
        style={{ clipPath: wwTornPath(edges, seed, amp), background: bg, color }}>
        {children}
      </div>
    </div>
  );
}

const WW_RANSOM_SWATCHES = [
  { bg: '#D2451E', fg: '#FAFAF8' },
  { bg: '#2B281F', fg: '#FAFAF8' },
  { bg: '#C7C24A', fg: '#2B281F' },
  { bg: '#8E8DB0', fg: '#2B281F' },
  { bg: '#FAFAF8', fg: '#2B281F' },
  { bg: '#6E6B3E', fg: '#FAFAF8' },
  { bg: 'transparent', fg: '#D2451E' },
  { bg: 'transparent', fg: '#2B281F' },
];

/** Ransom-note text. The display face already carries cut-out boxes, so `boxed`
 * defaults to false (just colour + rotation jitter). */
export function Ransom({
  text,
  size = 64,
  seed = 3,
  drop = false,
  gap = 2,
  boxed = false,
  style = {},
}: {
  text: string;
  size?: number;
  seed?: number;
  drop?: boolean;
  gap?: number;
  boxed?: boolean;
  style?: React.CSSProperties;
}) {
  const r = wwRng(seed);
  return (
    <span className="ww-ransom" style={{ gap, ...style }}>
      {text.split('').map((ch, k) => {
        if (ch === ' ')
          return <span key={k} style={{ width: size * 0.32, fontFamily: 'inherit' }} />;
        const sw = WW_RANSOM_SWATCHES[Math.floor(r() * WW_RANSOM_SWATCHES.length)];
        const rot = r() * 12 - 6;
        const dy = (r() * 0.14 - 0.07) * size;
        const fs = size * (0.86 + r() * 0.3);
        const drawBox = boxed && sw.bg !== 'transparent';
        const color = boxed ? sw.fg : sw.bg !== 'transparent' ? sw.bg : sw.fg;
        return (
          <span
            key={k}
            className={drop ? 'ww-drop' : ''}
            style={
              {
                '--r': rot + 'deg',
                fontSize: fs,
                color,
                background: drawBox ? sw.bg : 'transparent',
                transform: `rotate(${rot}deg) translateY(${dy}px)`,
                padding: drawBox ? '0.02em 0.12em' : '0 0.02em',
                border: drawBox ? '1.5px solid rgba(43,40,31,.5)' : 'none',
                boxShadow: drawBox ? '2px 2px 0 rgba(27,24,18,.30)' : 'none',
                animationDelay: drop ? k * 0.045 + 's' : undefined,
              } as React.CSSProperties
            }>
            {ch}
          </span>
        );
      })}
    </span>
  );
}

/** Scrolling marquee band. */
export function Marquee({
  items,
  bg = 'var(--ww-charcoal)',
  fg = 'var(--ww-chartreuse)',
  sep = '✕',
}: {
  items: string[];
  bg?: string;
  fg?: string;
  sep?: string;
}) {
  const row = items.join(`  ${sep}  `) + `  ${sep}  `;
  const span = (
    <span
      className="font-mono"
      style={{
        fontWeight: 600,
        fontSize: 15,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
        color: fg,
        paddingRight: 40,
      }}>
      {row}
    </span>
  );
  return (
    <div
      className="ww-marquee"
      style={{
        background: bg,
        borderTop: '3px solid var(--ww-charcoal)',
        borderBottom: '3px solid var(--ww-charcoal)',
        padding: '12px 0',
      }}>
      <div className="ww-marquee__track">
        {span}
        {span}
      </div>
    </div>
  );
}

/** Scattered decorative collage shape for hero backgrounds. */
export function CollageScrap({
  bob = false,
  style = {},
  children,
}: {
  bob?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn('ww-grain', bob && 'ww-bob')}
      style={{ position: 'absolute', boxShadow: 'var(--shadow-lift)', ...style }}>
      {children}
    </div>
  );
}

/** Striped image placeholder with mono label (used where no real image exists). */
export function ImgPlaceholder({
  label = 'image',
  w = '100%',
  h = 200,
  accent = '#8E8DB0',
  style = {},
}: {
  label?: string;
  w?: number | string;
  h?: number | string;
  accent?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        width: w,
        height: h,
        position: 'relative',
        overflow: 'hidden',
        background: `repeating-linear-gradient(135deg, ${accent}33, ${accent}33 11px, ${accent}1a 11px, ${accent}1a 22px)`,
        border: '2px solid var(--ww-charcoal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}>
      <span
        className="font-mono"
        style={{
          fontSize: 12,
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          color: 'var(--ww-charcoal)',
          background: 'var(--paper-50)',
          padding: '4px 9px',
          border: '1.5px solid var(--ww-charcoal)',
        }}>
        {label}
      </span>
    </div>
  );
}
